/*
  <anmls-slider> — the single slider used by the hero banner and the testimonials.

  Markup contract (see sections/anmls-hero-slider.liquid for a full example):

    <anmls-slider autoplay="5000" loop per-view="1" per-view-tablet="1" per-view-mobile="1">
      <div class="anmls-slider__viewport" data-viewport>
        <div class="anmls-slider__track" data-track>
          <div class="anmls-slider__slide">…</div>
        </div>
      </div>
      <button data-prev>…</button>
      <button data-next>…</button>
      <div data-dots></div>          <!-- dots are generated here -->
    </anmls-slider>

  Everything is optional: a slider with one slide silently disables its own controls.
  No dependencies, no theme globals — safe to drop into any Dawn install.
*/
(function () {
  if (customElements.get('anmls-slider')) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  class AnmlsSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.viewport = this.querySelector('[data-viewport]');
      if (!this.track) return;

      this.slides = Array.prototype.slice.call(this.track.children);
      this.index = 0;
      this.loop = this.hasAttribute('loop');
      this.autoplayMs = parseInt(this.getAttribute('autoplay'), 10) || 0;

      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.dotsHost = this.querySelector('[data-dots]');

      this.onPrev = this.prev.bind(this);
      this.onNext = this.next.bind(this);
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);

      this.buildDots();
      this.bindPointer();
      this.bindKeys();

      this.onResize = this.refresh.bind(this);
      window.addEventListener('resize', this.onResize, { passive: true });

      // Pause while off-screen or hovered, and while the tab is hidden.
      this.addEventListener('mouseenter', this.stop.bind(this));
      this.addEventListener('mouseleave', this.start.bind(this));
      this.addEventListener('focusin', this.stop.bind(this));
      this.addEventListener('focusout', this.start.bind(this));

      this.refresh();
      this.start();

      // Theme editor: re-measure when a block is added, removed or reordered.
      document.addEventListener('shopify:section:load', this.onResize);
      document.addEventListener('shopify:block:select', this.onBlockSelect.bind(this));
    }

    disconnectedCallback() {
      this.stop();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:section:load', this.onResize);
    }

    /* ------------------------------------------------------------ layout */

    get perView() {
      var w = window.innerWidth;
      var attr = 'per-view';
      if (w < 750) attr = 'per-view-mobile';
      else if (w < 1100) attr = 'per-view-tablet';
      var n = parseFloat(this.getAttribute(attr));
      if (!n || n <= 0) n = parseFloat(this.getAttribute('per-view')) || 1;
      return n;
    }

    get maxIndex() {
      return Math.max(0, this.slides.length - Math.floor(this.perView));
    }

    refresh() {
      var per = this.perView;
      var gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      // Each slide takes an equal share of the viewport minus the gaps between them.
      var basis = 'calc((100% - ' + (per - 1) * gap + 'px) / ' + per + ')';
      this.style.setProperty('--anmls-slide-basis', basis);
      if (this.index > this.maxIndex) this.index = this.maxIndex;
      this.apply();
    }

    apply() {
      var first = this.slides[0];
      if (!first) return;
      var gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      var step = first.getBoundingClientRect().width + gap;
      this.track.style.transform = 'translate3d(' + -(this.index * step) + 'px,0,0)';

      this.slides.forEach(function (slide, i) {
        var visible = i >= this.index && i < this.index + Math.ceil(this.perView);
        slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
        // Keep hidden slides out of the tab order.
        slide.querySelectorAll('a,button,input,select,textarea').forEach(function (el) {
          if (visible) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      }, this);

      if (this.dots) {
        this.dots.forEach(function (dot, i) {
          dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
        }, this);
      }
      if (!this.loop) {
        if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
        if (this.nextBtn) this.nextBtn.disabled = this.index >= this.maxIndex;
      }
    }

    /* ------------------------------------------------------- navigation */

    goTo(i) {
      var max = this.maxIndex;
      if (this.loop) {
        if (i < 0) i = max;
        else if (i > max) i = 0;
      } else {
        i = Math.min(Math.max(i, 0), max);
      }
      this.index = i;
      this.apply();
      this.dispatchEvent(new CustomEvent('anmls:slide', { detail: { index: i } }));
    }

    prev() { this.goTo(this.index - 1); }
    next() { this.goTo(this.index + 1); }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      var i = this.slides.indexOf(event.target.closest('.anmls-slider__slide'));
      if (i > -1) this.goTo(Math.min(i, this.maxIndex));
    }

    /* ------------------------------------------------------------- dots */

    buildDots() {
      if (!this.dotsHost) return;
      this.dotsHost.innerHTML = '';
      this.dots = [];
      var count = this.maxIndex + 1;
      if (count < 2) { this.dotsHost.hidden = true; return; }
      for (var i = 0; i < count; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'anmls-slider__dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', this.goTo.bind(this, i));
        this.dotsHost.appendChild(dot);
        this.dots.push(dot);
      }
    }

    /* ---------------------------------------------------- input handling */

    bindKeys() {
      this.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.prev(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
      }.bind(this));
    }

    bindPointer() {
      var startX = 0;
      var startY = 0;
      var dragging = false;

      this.track.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        dragging = true;
        startX = e.clientX;
        startY = e.clientY;
      });

      this.track.addEventListener('pointerup', function (e) {
        if (!dragging) return;
        dragging = false;
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;
        // Ignore mostly-vertical gestures so page scrolling still works on touch.
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
        if (dx < 0) this.next(); else this.prev();
      }.bind(this));

      this.track.addEventListener('pointercancel', function () { dragging = false; });
    }

    /* --------------------------------------------------------- autoplay */

    start() {
      this.stop();
      if (!this.autoplayMs || REDUCED.matches || this.maxIndex < 1) return;
      this.timer = setInterval(function () {
        if (document.hidden) return;
        if (!this.loop && this.index >= this.maxIndex) this.goTo(0);
        else this.next();
      }.bind(this), this.autoplayMs);
    }

    stop() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    }
  }

  customElements.define('anmls-slider', AnmlsSlider);
})();
